import { hydraUiApi, useHydraUIAPIQuery } from '@/services/api';
import type { UseTasksOptions } from '../types';
import { transformBackfill } from '../utils/task-transform';

type GetBackfillsParams = NonNullable<Parameters<typeof hydraUiApi.backfills.list>[0]>['params'];
type GetBackfillsQuery = NonNullable<NonNullable<GetBackfillsParams>['query']>;

export function useTasks(options: UseTasksOptions) {
  const { workspaceId, page, limit, pageSize, priority, enabled = true, state, type, refetchInterval } = options;

  // `page` is 1-based in the UI (Pluma); the list endpoint's `page` is 0-based
  // (visibleOffset = limit * page), so convert at the wire boundary.
  // The generated query types narrow `type`/`priority` to a subset of the enum
  // names the UI actually filters by, so the wire values are passed through the
  // generated type at the boundary.
  const query: GetBackfillsQuery = {
    page: page != null ? page - 1 : undefined,
    state: state ?? undefined,
    type: (type ?? undefined) as GetBackfillsQuery['type'],
    priority: (priority ?? undefined) as GetBackfillsQuery['priority'],
    limit: pageSize ?? undefined,
  };

  const { data, isLoading, refetch, dataUpdatedAt } = useHydraUIAPIQuery({
    queryKey: ['getBackfills', { params: { environment_id: workspaceId ?? 0 }, query }],
    queryFn: ({ signal }) =>
      hydraUiApi.backfills.list({
        params: { path: { environment_id: String(workspaceId ?? 0) }, query },
        signal,
      }),
    queryConfig: {
      enabled: enabled && workspaceId != null && workspaceId > 0,
      refetchInterval,
      select: (queryData) => ({
        ...queryData,
        backfills: (queryData.backfills ?? []).map(transformBackfill),
      }),
    },
  });

  // `limit` caps results client-side (compact widgets); server-side paging uses `pageSize`.
  const tasks = (limit != null && limit > 0 ? data?.backfills.slice(0, limit) : data?.backfills) ?? [];

  return {
    tasks,
    meta: data?.meta,
    isLoading,
    refetch,
    dataUpdatedAt,
  };
}
